/**
 * Helpers to copy rendered spec content in two useful formats:
 *  - Markdown (headings, GFM tables, lists) — for pasting into Markdown editors.
 *  - Rich text (HTML) — for pasting into Word / Google Docs with real tables.
 *
 * Both operate on a live rendered DOM node so structure/formatting is preserved.
 */

/** Convert inline nodes (text + strong/em/code/a/br) to Markdown. */
function inlineToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').replace(/\s+/g, ' ')
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return ''
  const el = node as HTMLElement
  const tag = el.tagName.toLowerCase()
  const inner = Array.from(el.childNodes).map(inlineToMarkdown).join('')
  switch (tag) {
    case 'strong':
    case 'b':
      return inner.trim() ? `**${inner}**` : inner
    case 'em':
    case 'i':
      return inner.trim() ? `*${inner}*` : inner
    case 'code':
      return '`' + inner + '`'
    case 'a': {
      const href = el.getAttribute('href')
      return href ? `[${inner}](${href})` : inner
    }
    case 'br':
      return '  \n'
    default:
      return inner
  }
}

function cellToMarkdown(cell: Element): string {
  return inlineToMarkdown(cell).replace(/\|/g, '\\|').replace(/\s*\n\s*/g, ' ').trim()
}

function tableToMarkdown(table: HTMLElement): string {
  const rows = Array.from(table.querySelectorAll('tr'))
  if (!rows.length) return ''
  const matrix = rows.map(r => Array.from(r.querySelectorAll('th,td')).map(cellToMarkdown))
  const cols = Math.max(...matrix.map(r => r.length))
  const pad = (r: string[]) => { while (r.length < cols) r.push(''); return r }
  const header = pad(matrix[0])
  const lines = [
    '| ' + header.join(' | ') + ' |',
    '| ' + header.map(() => '---').join(' | ') + ' |',
    ...matrix.slice(1).map(r => '| ' + pad(r).join(' | ') + ' |'),
  ]
  return lines.join('\n')
}

function listToMarkdown(list: HTMLElement, ordered: boolean, depth: number): string {
  const items = Array.from(list.children).filter(c => c.tagName.toLowerCase() === 'li')
  return items.map((li, i) => {
    const prefix = ordered ? `${i + 1}. ` : '- '
    let text = ''
    const nested: string[] = []
    for (const child of Array.from(li.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const t = (child as HTMLElement).tagName.toLowerCase()
        if (t === 'ul' || t === 'ol') {
          nested.push(listToMarkdown(child as HTMLElement, t === 'ol', depth + 1))
          continue
        }
      }
      text += inlineToMarkdown(child)
    }
    let out = '  '.repeat(depth) + prefix + text.trim()
    if (nested.length) out += '\n' + nested.join('\n')
    return out
  }).join('\n')
}

function blocksToMarkdown(root: HTMLElement): string {
  const out: string[] = []
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const t = (child.textContent ?? '').trim()
      if (t) out.push(t)
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue
    const el = child as HTMLElement
    const tag = el.tagName.toLowerCase()
    switch (tag) {
      case 'h1': out.push('# ' + inlineToMarkdown(el).trim()); break
      case 'h2': out.push('## ' + inlineToMarkdown(el).trim()); break
      case 'h3': out.push('### ' + inlineToMarkdown(el).trim()); break
      case 'h4': out.push('#### ' + inlineToMarkdown(el).trim()); break
      case 'h5': out.push('##### ' + inlineToMarkdown(el).trim()); break
      case 'h6': out.push('###### ' + inlineToMarkdown(el).trim()); break
      case 'p': { const s = inlineToMarkdown(el).trim(); if (s) out.push(s); break }
      case 'ul': out.push(listToMarkdown(el, false, 0)); break
      case 'ol': out.push(listToMarkdown(el, true, 0)); break
      case 'table': out.push(tableToMarkdown(el)); break
      case 'blockquote': out.push(inlineToMarkdown(el).trim().split('\n').map(l => '> ' + l).join('\n')); break
      case 'pre': out.push('```\n' + (el.textContent ?? '').replace(/\s+$/, '') + '\n```'); break
      case 'hr': out.push('---'); break
      case 'br': break
      default: {
        // Container-ish elements: recurse; leaf-ish: take inline text
        const hasBlockChild = el.querySelector('h1,h2,h3,h4,h5,h6,p,ul,ol,table,blockquote,pre,div')
        if (hasBlockChild) {
          const nested = blocksToMarkdown(el).trim()
          if (nested) out.push(nested)
        } else {
          const s = inlineToMarkdown(el).trim()
          if (s) out.push(s)
        }
      }
    }
  }
  return out.join('\n\n')
}

/** Convert a rendered element's content to Markdown text. */
export function elementToMarkdown(root: HTMLElement): string {
  return blocksToMarkdown(root).replace(/\n{3,}/g, '\n\n').trim()
}

/**
 * Copy an element's rich HTML to the clipboard (with plain-text fallback),
 * so it pastes into Word / Google Docs preserving tables and formatting.
 * Returns false if the browser has no rich-clipboard support (caller can fall back).
 */
export async function copyRichContent(root: HTMLElement): Promise<boolean> {
  const html = `<meta charset="utf-8">${root.innerHTML}`
  const text = root.innerText
  const w = window as unknown as { ClipboardItem?: typeof ClipboardItem }
  if (navigator.clipboard && typeof w.ClipboardItem === 'function') {
    const item = new w.ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    })
    await navigator.clipboard.write([item])
    return true
  }
  await navigator.clipboard.writeText(text)
  return false
}
